import { filterByNormalizedText } from '@/utils/textSearch';
import { isValidHolidayPeriod, planningStatusLabels } from './holidayCalendar';

// The planning endpoint returns all employees for the requested period.
// Filter the returned rows locally so matching never depends on API accent handling.
export function filterVacationPlanning(employees, name) {
  return filterByNormalizedText(
    Array.isArray(employees) ? employees : [],
    String(name ?? '').trim(),
    employee => employee.fullName || [employee.name, employee.lastName].filter(Boolean).join(' '),
  );
}

// Sum complete API day counts for visible requests, without prorating the period.
export function vacationPlanningDays(employees, status) {
  return (Array.isArray(employees) ? employees : []).reduce((total, employee) =>
    total + (employee.holidays || []).reduce((sum, holiday) => {
      if (holiday.status !== status) return sum;
      const value = holiday.daysConsumed;
      if (typeof value !== 'number' && typeof value !== 'string') return sum;
      const days = Number(value);
      return sum + (Number.isFinite(days) ? days : 0);
    }, 0), 0);
}

export const approvedVacationPlanningDays = employees => vacationPlanningDays(employees, 'approved');

export function planningStatuses(status = 'approved') {
  if (status === 'pending_approved') return ['approved', 'pending'];
  if (!status || status === 'all') return ['pending', 'approved', 'rejected', 'cancelled'];
  return String(status).split(',');
}

export function matchesPlanningStatus(holiday, status = 'approved') {
  return planningStatuses(status).includes(holiday.status);
}

export const planningStatusParam = status => planningStatuses(status).join(',');

export async function loadVacationPlanning(request, params, status) {
  const response = await request({ ...params, status: planningStatusParam(status) });
  if (!Array.isArray(response?.data?.data)) throw new Error('Respuesta del plan inválida');
  const employees = new Map();
  response.data.data.forEach(employee => {
    if (!employee?.id || !Array.isArray(employee.holidays)) throw new Error('Respuesta del plan inválida');
    const key = String(employee.id);
    if (!employees.has(key)) employees.set(key, { ...employee, holidays: [] });
    const entry = employees.get(key);
    const seen = new Set(entry.holidays.map(holiday => String(holiday.id)));
    employee.holidays.forEach(holiday => {
      if (!holiday?.id || !Object.hasOwn(planningStatusLabels, holiday.status) ||
          !isValidHolidayPeriod(holiday.fromDate, holiday.toDate))
        throw new Error('El plan requiere vacaciones con id, estado y fechas válidos');
      if (!matchesPlanningStatus(holiday, status)) return;
      const id = String(holiday.id);
      if (!seen.has(id)) entry.holidays.push({ ...holiday });
      seen.add(id);
    });
  });
  return [...employees.values()].filter(employee => employee.holidays.length);
}
